// ---------------------------------------------------------------------------
// FlowStepPanel — Renders the step list for the currently selected flow.
//
// Replaces the blank FlowCanvas when a flow is selected, showing a header
// with flow name + trigger info, followed by FlowStepCard for each step.
// ---------------------------------------------------------------------------

import { useMemo, useCallback, useState, useEffect, useRef, Fragment } from 'react'
import { Zap, PlayCircle, GitBranch, Info, Plus, Link, GripVertical, Play, Loader2, ChevronDown, ChevronRight } from 'lucide-react'
import type { LucideIcon } from 'lucide-react'
import type { Flow, FlowStep, EventBinding } from '@cogo/types-v2'
import type { FlowMeta } from '@/features/flow-editor/models/flow-models'
import { cn } from '@/lib/utils'
import { useFlowEditorStore } from '@/features/flow-editor/store/flow-editor-store'
import { useStudioStore } from '@/store/studio-store'
import { Popover, PopoverTrigger, PopoverContent } from '@/components/ui/popover'
import { toast } from '@/store/toast-store'
import { DndContext, closestCenter } from '@dnd-kit/core'
import type { DragEndEvent } from '@dnd-kit/core'
import { SortableContext, verticalListSortingStrategy, useSortable } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { findFlowOwner, findFlowTriggers, flowNamesMatch, resolveFlowOwner } from '@/features/flow-editor/services/flow-ownership'
import type { OwnedBlock } from '@/features/flow-editor/services/flow-ownership'
import { dispatchFlowStepsOp } from '@/features/flow-editor/lib/flow-steps-dispatch'
import { resolveStepAtPath } from '@/features/flow-editor/store/flow-slice'
import type { NestedStepPath, StepPath } from '@/features/flow-editor/store/flow-slice'
import { runFlowDryRun } from '@/features/preview/services/actionflow-trace'
import { usePreviewTestingStore } from '@/features/preview/store/preview-testing-store'
import { ActionFlowPreviewOverlay } from '@/features/preview/components/actionflow-preview-overlay'
import { FlowStepCard } from './flow-step-card'
import { AddStepLine } from './add-step-popover'

/**
 * Cheap structural equality for step arrays. The common case (the auto-save
 * bridge mirroring the buffer back into currentProject) is reference-equal;
 * reload-shaped updates fall back to a JSON comparison.
 */
function stepsStructurallyEqual(a: FlowStep[], b: FlowStep[]): boolean {
  if (a === b) return true
  if (a.length !== b.length) return false
  try {
    return JSON.stringify(a) === JSON.stringify(b)
  } catch {
    return false
  }
}

// ---------------------------------------------------------------------------
// Trigger icon map
// ---------------------------------------------------------------------------

const TRIGGER_ICONS: Record<string, LucideIcon> = {
  event: Zap,
  init: PlayCircle,
  lifecycle: PlayCircle,
  manual: GitBranch,
}

// findFlowTriggers / findFlowOwner moved to services/flow-ownership.ts so the
// auto-save bridge routes saves with the exact same owner resolution this
// panel uses for display and step seeding (F58).

// ---------------------------------------------------------------------------
// SortableStepItem — drag handle wrapper for FlowStepCard
// ---------------------------------------------------------------------------

function SortableStepItem({
  id,
  step,
  index,
  isSelected,
  onSelect,
  onNestedSelect,
}: {
  id: string
  step: FlowStep
  index: number
  isSelected: boolean
  onSelect: (idx: number) => void
  onNestedSelect?: (path: NestedStepPath) => void
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id })
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  }
  return (
    <div ref={setNodeRef} style={style} className="flex items-start gap-1">
      <button
        {...attributes}
        {...listeners}
        className="mt-2 p-0.5 text-muted-foreground/40 hover:text-muted-foreground cursor-grab active:cursor-grabbing shrink-0"
        tabIndex={-1}
        title="Drag to reorder"
      >
        <GripVertical className="h-3.5 w-3.5" />
      </button>
      <div className="flex-1 min-w-0">
        <FlowStepCard
          step={step}
          index={index}
          isSelected={isSelected}
          onSelect={onSelect}
          onNestedSelect={onNestedSelect}
        />
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// FlowStepPanel
// ---------------------------------------------------------------------------

export function FlowStepPanel() {
  const selectedFlowId = useFlowEditorStore((s) => s.selectedFlowId)
  const recoverFlowSteps = useFlowEditorStore((s) => s.recoverFlowSteps)
  const flows = useFlowEditorStore((s) => s.flows)
  const selectedEventContext = useFlowEditorStore((s) => s.selectedEventContext)
  const selectedStepIndex = useFlowEditorStore((s) => s.selectedStepIndex)
  const setSelectedStepIndex = useFlowEditorStore((s) => s.setSelectedStepIndex)
  const setSelectedStepPath = useFlowEditorStore((s) => s.setSelectedStepPath)
  const setInspectorOpen = useFlowEditorStore((s) => s.setInspectorOpen)
  const flowSteps = useFlowEditorStore((s) => s.flowSteps)
  const setFlowSteps = useFlowEditorStore((s) => s.setFlowSteps)
  const ackFlowStepsSeed = useFlowEditorStore((s) => s.ackFlowStepsSeed)
  const project = useStudioStore((s) => s.currentProject)
  const blocks = project?.blocks ?? {}
  const dryRunTrace = usePreviewTestingStore((s) => s.dryRunTrace)

  // Find the selected flow's metadata from the editor flow list
  const selectedFlowMeta = useMemo(
    () => flows.find((f: { id: string }) => f.id === selectedFlowId),
    [flows, selectedFlowId],
  )

  const flowName = selectedFlowMeta?.name ?? null
  const nodes = useFlowEditorStore((s) => s.nodes)

  // Find the actual flow definition (with steps) from block data
  // Flow names may be namespaced (e.g. "store-nav.addToCart") — try both full and bare name
  const flowData = useMemo(() => {
    if (!flowName || !blocks || Object.keys(blocks).length === 0) return null
    const typedBlocks = blocks as Record<string, { flow?: Record<string, unknown> }>
    // Try full name first
    const result = findFlowOwner(typedBlocks, flowName)
    if (result) return result
    // Try bare name (after last dot)
    const dotIdx = flowName.lastIndexOf('.')
    if (dotIdx > 0) {
      const bareName = flowName.slice(dotIdx + 1)
      const result2 = findFlowOwner(typedBlocks, bareName)
      if (result2) return result2
    }
    return null
  }, [flowName, blocks])

  // F50 recovery affordance: load-time detection (toLoadedApp) recorded an
  // intact shadowed copy for this flow while the served owner copy is empty.
  // Same full-then-bare name convention as the owner lookup above.
  const recoverable = useMemo(() => {
    const map = (project as { recoverableFlows?: Record<string, { ownerBlock: string; shadowBlock: string; steps: FlowStep[] }> } | null)?.recoverableFlows
    if (!map || !flowName) return null
    if (map[flowName]) return map[flowName]
    const dotIdx = flowName.lastIndexOf('.')
    if (dotIdx > 0) return map[flowName.slice(dotIdx + 1)] ?? null
    return null
  }, [project, flowName])

  // Fallback: derive steps from canvas nodes when block data is not yet loaded
  const stepsFromNodes: FlowStep[] = useMemo(() => {
    if (flowData) return [] // block data available, don't need fallback
    if (!nodes || nodes.length === 0) return []
    // Filter out trigger/stickyNote nodes, keep executable step nodes
    return nodes
      .filter((n) => n.nodeType !== 'trigger' && n.nodeType !== 'stickyNote')
      .map((n) => ({ action: n.actionType, params: n.parameters ?? {} }) as unknown as FlowStep)
  }, [flowData, nodes])

  // Seed editable steps from the selected flow. The effect re-runs on every
  // currentProject identity change (the auto-save bridge mirrors each step
  // edit back into currentProject), so it must distinguish three cases (QA-3):
  //   1. Same flow, identical content → acknowledge only. The buffer, the
  //      step selection, and the mounted form instances all survive; the
  //      stale dirty flag is cleared exactly like a real seed would (#969).
  //   2. Same flow, changed content → re-seed; keep the selection only while
  //      it still resolves against the incoming steps.
  //   3. Different flow (or first seed) → reset selection and re-seed.
  useEffect(() => {
    const nextSteps = flowData?.flow.steps ?? stepsFromNodes
    const store = useFlowEditorStore.getState()
    const sameFlow = store.flowStepsFlowId != null && store.flowStepsFlowId === selectedFlowId

    if (sameFlow && stepsStructurallyEqual(store.flowSteps, nextSteps)) {
      ackFlowStepsSeed(selectedFlowId)
      return
    }

    const selection: StepPath | null =
      store.selectedStepPath ?? (store.selectedStepIndex != null ? [store.selectedStepIndex] : null)
    const keepSelection = sameFlow && selection != null && resolveStepAtPath(nextSteps, selection) != null
    if (!keepSelection) {
      setSelectedStepIndex(null)
      setSelectedStepPath(null)
    }
    setFlowSteps(nextSteps, selectedFlowId)
  }, [ackFlowStepsSeed, flowData, selectedFlowId, setFlowSteps, setSelectedStepIndex, setSelectedStepPath, stepsFromNodes])

  // Find event triggers for this flow
  const triggers = useMemo(() => {
    if (!flowName || !blocks) return []
    return findFlowTriggers(
      blocks as Record<string, { events?: Record<string, Record<string, EventBinding>> }>,
      flowName,
    )
  }, [flowName, blocks])

  // Mirrors the auto-save bridge's owner routing (auto-save-bridge.ts):
  // declared/event-bound owner first, else the first block. Only a project
  // with ZERO blocks leaves a flow unroutable — the bridge then drops step
  // saves silently while the header still says "Saved" (QA-2b). Surface it.
  const unroutable = useMemo(() => {
    if (!flowName) return false
    const owned = (blocks ?? {}) as Record<string, OwnedBlock>
    const resolved = resolveFlowOwner(owned, flowName)
    const ownerBlockName = resolved?.blockName ?? Object.keys(owned)[0]
    return ownerBlockName == null
  }, [flowName, blocks])

  // QA-2b follow-through: while the project had ZERO blocks, the bridge
  // dropped step saves without recording its dedupe snapshot or marking
  // dirty. When the first block lands (unroutable true → false) with unsaved
  // step edits still in the buffer, re-emit the flow-editor store state so
  // the bridge's subscription re-runs owner routing immediately — otherwise
  // the work sat unflushed (header "Saved", chip gone) until the next
  // flow-editor interaction, and a reload right after adding the block lost
  // the steps. (The bridge's OTHER drop path — blocks exist but blockIdMap
  // lags — already marks dirty and retries on the next store event.)
  const wasUnroutableRef = useRef(unroutable)
  useEffect(() => {
    const was = wasUnroutableRef.current
    wasUnroutableRef.current = unroutable
    if (was && !unroutable && useFlowEditorStore.getState().flowStepsDirty) {
      useFlowEditorStore.setState({})
    }
  }, [unroutable])

  // ---- Run (dry) — execute the live step buffer off-canvas (F5) ----
  // Runs the selected flow through a fresh headless runtime-v2 engine with an
  // empty mock context and writes the per-step trace into the store's DEDICATED
  // dryRunTrace slot (NOT the shared preview buffer), so the result shows in the
  // dry-run results panel below WITHOUT (a) evicting a real preview trace from
  // the bounded-12 buffer (badge can't regress) or (b) leaking into the live
  // overlay/inspector/chat bubble (which read actionFlowTraces only). The trace
  // is marked source:'dry-run' and the run suppresses network + side effects
  // (see runFlowDryRun) so the panel's "live state untouched" label is honest.
  const [dryRunning, setDryRunning] = useState(false)
  const [resultsOpen, setResultsOpen] = useState(false)
  const handleDryRun = useCallback(async () => {
    const store = useFlowEditorStore.getState()
    const meta = store.flows.find((f) => f.id === store.selectedFlowId)
    if (!meta) return
    // Ownership guard (mirrors the seed effect at :192 and StepInspector): only
    // dry-run when the live buffer actually belongs to the selected flow. After
    // selectFlow the buffer still holds the PREVIOUS flow's steps until the
    // panel re-seeds (flow-slice selectFlow comment) — without this check a
    // click in that window would run the OLD steps labeled with the NEW flow's
    // name (L4-CA-04). When the buffer is foreign, skip rather than mislabel.
    if (store.flowStepsFlowId !== store.selectedFlowId) return
    const steps = store.flowSteps
    if (steps.length === 0) return
    // Assemble the Flow from the LIVE editing buffer (unsaved edits included).
    const flow: Flow = {
      name: meta.name,
      trigger: meta.trigger,
      steps,
    }
    setDryRunning(true)
    setResultsOpen(true)
    try {
      const entry = await runFlowDryRun(flow)
      // Dedicated slot — never the shared preview buffer. The result panel
      // reads dryRunTrace correlated to the flow it was run against.
      usePreviewTestingStore.getState().setDryRunTrace(entry)
    } finally {
      setDryRunning(false)
    }
  }, [])

  // Correlate the dry-run result to the SELECTED flow: only show the dry-run
  // trace when it was run against the flow currently open (full-then-bare name
  // rule, both directions). After selecting flow B the dry-run for flow A is
  // not shown under B's panel (L4-CORR-2 scenario b). null = nothing to show.
  const currentDryRun = useMemo(() => {
    if (!dryRunTrace || !flowName) return null
    const matches =
      flowNamesMatch(flowName, dryRunTrace.flowName) ||
      flowNamesMatch(dryRunTrace.flowName, flowName)
    return matches ? dryRunTrace : null
  }, [dryRunTrace, flowName])

  // Reset the open/closed state ONLY when the selected flow actually changes,
  // so selecting flow B never inherits flow A's panel-open state. A fresh
  // dry-run of the current flow sets resultsOpen=true itself (handleDryRun) and
  // is not disturbed here — keyed on selectedFlowId, not on currentDryRun, to
  // avoid collapsing the panel during the in-flight run before the trace lands.
  const lastFlowIdRef = useRef(selectedFlowId)
  useEffect(() => {
    if (lastFlowIdRef.current !== selectedFlowId) {
      lastFlowIdRef.current = selectedFlowId
      setResultsOpen(false)
    }
  }, [selectedFlowId])

  const handleDragEnd = useCallback((event: DragEndEvent) => {
    const { active, over } = event
    if (!over || active.id === over.id) return
    const from = Number(active.id)
    const to = Number(over.id)
    if (!isNaN(from) && !isNaN(to)) {
      // Reorders dispatch through the MCP bus so the undo middleware records
      // them (F4) — see flow-steps-dispatch.ts.
      dispatchFlowStepsOp('reorderSteps', [from, to])
    }
  }, [])

  const handleNestedSelect = useCallback((path: NestedStepPath) => {
    setSelectedStepPath(path)
    setInspectorOpen(true)
  }, [setSelectedStepPath, setInspectorOpen])

  const handleGoToEvents = useCallback(() => {
    // In unified panel, "Triggered by" just highlights the corresponding event
    // No tab switching needed — tree is always visible on left
    if (triggers.length > 0) {
      const t = triggers[0]
      useFlowEditorStore.getState().setHighlightedEventId(
        `event:${t.blockName}:${t.elementId}:${t.eventType}`
      )
    }
  }, [triggers])

  // ---- Create / Bind handlers ----

  const handleCreateFlow = useCallback(() => {
    if (!selectedEventContext) return
    const { blockName, elementId, eventType } = selectedEventContext
    // Auto-name: on{EventType}_{elementLabel} e.g. "onTap_addToCart"
    const label = elementId.replace(/^\{ROLE:(.+)\}$/, '$1').replace(/-/g, '_')
    const flowName = `on${eventType.charAt(0).toUpperCase() + eventType.slice(1)}_${label}`
    const store = useFlowEditorStore.getState()
    const flowId = store.createFlow(flowName, 'event')
    if (flowId === null) {
      // Duplicate name (F8 guard) — keep the event context so the user can
      // bind the existing flow instead.
      toast.error(`A flow named "${flowName}" already exists`)
      return
    }
    store.bindFlowToEvent(flowId, blockName, elementId, eventType)
    store.setSelectedEventContext(null)
    toast.success(`Created flow: ${flowName}`)
  }, [selectedEventContext])

  const handleBindExisting = useCallback(
    (flowId: string) => {
      if (!selectedEventContext) return
      const { blockName, elementId, eventType } = selectedEventContext
      const store = useFlowEditorStore.getState()
      store.selectFlow(flowId)
      store.bindFlowToEvent(flowId, blockName, elementId, eventType)
      store.setSelectedEventContext(null)
      const flowMeta = store.flows.find((f) => f.id === flowId)
      toast.success(`Bound flow: ${flowMeta?.name ?? flowId}`)
    },
    [selectedEventContext],
  )

  // ---- Empty state: unbound event clicked → Create/Bind UI ----
  if (!selectedFlowId && selectedEventContext) {
    return (
      <div className="flex flex-col h-full overflow-hidden">
        {/* Event context header */}
        <div className="px-4 py-3 border-b border-border bg-background">
          <div className="flex items-center gap-2 text-xs">
            <Zap className="h-3.5 w-3.5 text-muted-foreground" />
            <span className="font-mono text-muted-foreground">{selectedEventContext.elementId}</span>
            <span className="text-[9px] px-1.5 py-0.5 rounded bg-muted text-muted-foreground uppercase">
              {selectedEventContext.eventType}
            </span>
          </div>
          <div className="mt-0.5 text-[10px] text-muted-foreground">
            Block: <span className="font-mono">{selectedEventContext.blockName}</span>
          </div>
        </div>

        {/* Create/Bind actions */}
        <div className="flex-1 flex flex-col items-center justify-center px-6 gap-4">
          <div className="text-center">
            <GitBranch className="h-8 w-8 text-muted-foreground/30 mx-auto mb-2" />
            <p className="text-sm text-muted-foreground mb-1">No ActionFlow connected</p>
            <p className="text-xs text-muted-foreground/60">Create a new flow or bind an existing one</p>
          </div>

          {/* Create button */}
          <button
            className="w-full max-w-[200px] flex items-center justify-center gap-2 rounded-md bg-primary text-primary-foreground px-4 py-2 text-sm font-medium hover:bg-primary/90 transition-colors"
            onClick={handleCreateFlow}
          >
            <Plus className="h-4 w-4" />
            Create ActionFlow
          </button>

          {/* Bind existing dropdown */}
          <div className="w-full max-w-[200px]">
            <BindFlowDropdown
              flows={flows}
              blocks={blocks as Record<string, unknown>}
              currentBlockName={selectedEventContext.blockName}
              onSelect={handleBindExisting}
            />
          </div>
        </div>
      </div>
    )
  }

  // ---- Empty state: no flow selected, no event context ----
  if (!selectedFlowId || !flowName) {
    return (
      <div className="flex flex-col items-center justify-center h-full text-center px-6">
        <GitBranch className="h-8 w-8 text-muted-foreground/40 mb-3" />
        <p className="text-sm text-muted-foreground">
          Select a flow from the list to view its steps
        </p>
      </div>
    )
  }

  const triggerType = selectedFlowMeta?.trigger ?? 'event'
  const TriggerIcon = TRIGGER_ICONS[triggerType] ?? Zap
  const steps: FlowStep[] = flowSteps

  return (
    <div className="flex flex-col h-full overflow-hidden">
      {/* Header */}
      <div className="shrink-0 px-4 py-3 border-b border-border bg-background">
        <div className="flex items-center gap-2">
          <TriggerIcon className="h-4 w-4 shrink-0 text-primary" />
          <span className="text-sm font-semibold truncate">{flowName}</span>
          <span className={cn(
            'shrink-0 text-[9px] px-1.5 py-0.5 rounded-md font-medium uppercase tracking-wider',
            triggerType === 'event' && 'bg-blue-500/10 text-blue-600 dark:text-blue-400',
            triggerType === 'init' && 'bg-primary/10 text-primary dark:text-primary',
            triggerType === 'lifecycle' && 'bg-purple-500/10 text-purple-600 dark:text-purple-400',
            triggerType === 'manual' && 'bg-amber-500/10 text-amber-600 dark:text-amber-400',
          )}>
            {triggerType}
          </span>
          {/* Run (dry) — execute the live step buffer through a headless engine */}
          <button
            type="button"
            data-testid="flow-dry-run-button"
            className="ml-auto shrink-0 inline-flex items-center gap-1 rounded-md border border-border px-2 py-1 text-[11px] font-medium text-foreground hover:bg-accent/50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            onClick={handleDryRun}
            disabled={dryRunning || steps.length === 0}
            title={
              steps.length === 0
                ? 'Add a step to dry-run this flow'
                : 'Run this flow once through a headless engine with an empty context — network & side effects are stubbed, no live state is touched'
            }
          >
            {dryRunning ? (
              <Loader2 className="h-3 w-3 animate-spin" />
            ) : (
              <Play className="h-3 w-3" />
            )}
            {dryRunning ? 'Running…' : 'Run (dry)'}
          </button>
        </div>

        {/* Triggered by links */}
        {triggers.length > 0 && (
          <div className="mt-1.5 flex flex-wrap items-center gap-1">
            <span className="text-[10px] text-muted-foreground">Triggered by:</span>
            {triggers.map((t, i) => (
              <button
                key={`${t.elementId}-${t.eventType}-${i}`}
                type="button"
                className="inline-flex items-center gap-0.5 text-[10px] text-primary/80 hover:text-primary hover:underline transition-colors"
                onClick={handleGoToEvents}
                title={`Go to events tab: ${t.elementId} on ${t.eventType}`}
              >
                <Zap className="h-2.5 w-2.5" />
                <span className="font-mono">{t.elementId}</span>
                <span className="text-muted-foreground">on {t.eventType}</span>
              </button>
            ))}
          </div>
        )}

        {/* Owner block */}
        {flowData?.blockName && (
          <div className="mt-1 text-[10px] text-muted-foreground">
            Block: <span className="font-mono">{flowData.blockName}</span>
          </div>
        )}

        {/* Unroutable flow — no block to save into (QA-2b) */}
        {unroutable && (
          <div
            data-testid="flow-unroutable-warning"
            className="mt-1.5 flex items-start gap-1.5 rounded-md border border-amber-500/35 bg-amber-500/10 px-2 py-1.5 text-[10px] text-amber-700 dark:text-amber-300"
          >
            <Info className="h-3 w-3 shrink-0 mt-px" />
            <span>
              This flow has nowhere to save yet — add a block first. Steps are not saved and will be lost if you reload or switch flows.
            </span>
          </div>
        )}
      </div>

      {/* Steps list */}
      <div className="flex-1 overflow-y-auto px-3 py-3">
        {steps.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-8 text-center">
            <Info className="h-5 w-5 text-muted-foreground/40 mb-2" />
            <p className="text-xs text-muted-foreground">
              This flow has no steps defined yet.
            </p>
            {recoverable && (
              <div
                data-testid="flow-recovery-banner"
                className="mt-3 flex flex-col items-center gap-2 rounded-md border border-amber-500/35 bg-amber-500/10 px-3 py-2 text-[10px] text-amber-700 dark:text-amber-300"
              >
                <span>
                  The saved copy of this flow is empty, but an intact copy with{' '}
                  {recoverable.steps.length} step{recoverable.steps.length !== 1 ? 's' : ''} was
                  found on block &ldquo;{recoverable.shadowBlock}&rdquo; (pre-fix auto-save
                  artifact). Recovering loads those steps here and saves them on this
                  flow&apos;s owner block.
                </span>
                <button
                  type="button"
                  data-testid="flow-recovery-button"
                  className="rounded border border-amber-600/50 px-2 py-0.5 text-[10px] font-medium hover:bg-amber-500/20 transition-colors"
                  onClick={() => recoverFlowSteps(recoverable.steps, selectedFlowId)}
                >
                  Recover {recoverable.steps.length} step{recoverable.steps.length !== 1 ? 's' : ''}
                </button>
              </div>
            )}
            <div className="mt-3">
              <AddStepLine index={0} label="Add first step" alwaysVisible />
            </div>
          </div>
        ) : (
          <DndContext collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
            <SortableContext
              items={steps.map((_, i) => String(i))}
              strategy={verticalListSortingStrategy}
            >
              <div className="space-y-0">
                <AddStepLine index={0} />
                {steps.map((step, i) => (
                  <Fragment key={i}>
                    <SortableStepItem
                      id={String(i)}
                      step={step}
                      index={i}
                      isSelected={selectedStepIndex === i}
                      onSelect={(idx) => {
                        setSelectedStepIndex(idx)
                        setSelectedStepPath([idx])
                        setInspectorOpen(true)
                      }}
                      onNestedSelect={handleNestedSelect}
                    />
                    <AddStepLine index={i + 1} />
                  </Fragment>
                ))}
              </div>
            </SortableContext>
          </DndContext>
        )}
      </div>

      {/* Dry-run results — the trace surface the Run-(dry) button feeds. Reads
          the store's DEDICATED dryRunTrace slot (NOT the shared preview buffer)
          correlated to the selected flow, so it only ever renders THIS flow's
          dry-run — never a real preview run or another flow's stale trace under
          the "live state untouched" header. Collapsible; only shown when this
          flow actually has a dry-run result. */}
      {currentDryRun && resultsOpen && (
        <div
          data-testid="flow-dry-run-results"
          className="shrink-0 border-t border-border bg-background"
        >
          <button
            type="button"
            className="flex w-full items-center gap-1.5 px-4 py-1.5 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground hover:bg-accent/40 transition-colors"
            onClick={() => setResultsOpen((open) => !open)}
            aria-expanded={resultsOpen}
          >
            <ChevronDown className="h-3 w-3" />
            Dry-run result
            <span className="ml-1 font-normal normal-case tracking-normal text-muted-foreground/70">
              empty context · network &amp; side effects stubbed · live state untouched
            </span>
          </button>
          {/* Relative wrapper so the absolutely-positioned overlay anchors here
              instead of the preview canvas; capped height keeps the step list usable. */}
          <div className="relative h-[min(320px,40vh)] min-h-[220px] overflow-hidden">
            <ActionFlowPreviewOverlay trace={currentDryRun} />
          </div>
        </div>
      )}
      {currentDryRun && !resultsOpen && (
        <button
          type="button"
          data-testid="flow-dry-run-results-collapsed"
          className="shrink-0 flex w-full items-center gap-1.5 px-4 py-1 border-t border-border bg-background text-[10px] text-muted-foreground hover:bg-accent/40 transition-colors"
          onClick={() => setResultsOpen(true)}
        >
          <ChevronRight className="h-3 w-3" />
          Dry-run result
        </button>
      )}

      {/* Footer: step count */}
      <div className="shrink-0 px-4 py-1.5 border-t border-border bg-background text-[10px] text-muted-foreground">
        {steps.length} step{steps.length !== 1 ? 's' : ''}
        {flowData?.flow.debounce ? ` | debounce: ${flowData.flow.debounce}ms` : ''}
        {flowData?.flow.priority != null ? ` | priority: ${flowData.flow.priority}` : ''}
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// BindFlowDropdown — local helper for binding existing flows
// ---------------------------------------------------------------------------

function BindFlowDropdown({
  flows,
  blocks,
  currentBlockName,
  onSelect,
}: {
  flows: FlowMeta[]
  blocks: Record<string, unknown>
  currentBlockName: string
  onSelect: (flowId: string) => void
}) {
  const [open, setOpen] = useState(false)

  // Group flows: current block first, then others
  const currentBlockFlows = flows.filter((f) => {
    const block = blocks[currentBlockName] as { flow?: Record<string, unknown> } | undefined
    return block?.flow?.[f.name] != null
  })
  const otherFlows = flows.filter((f) => !currentBlockFlows.includes(f))

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button className="w-full flex items-center justify-center gap-2 rounded-md border border-border px-4 py-2 text-sm text-muted-foreground hover:bg-accent/50 transition-colors">
          <Link className="h-4 w-4" />
          Bind existing flow
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-[240px] p-0" align="center">
        <div className="max-h-[200px] overflow-y-auto">
          {currentBlockFlows.length > 0 && (
            <>
              <div className="px-3 py-1.5 text-[10px] font-semibold text-muted-foreground uppercase tracking-wider bg-muted/50">
                Current block
              </div>
              {currentBlockFlows.map((f) => (
                <button
                  key={f.id}
                  className="w-full px-3 py-1.5 text-xs text-left hover:bg-accent flex items-center gap-2"
                  onClick={() => {
                    onSelect(f.id)
                    setOpen(false)
                  }}
                >
                  <Zap className="h-3 w-3 text-muted-foreground" />
                  <span className="truncate font-mono">{f.name}</span>
                  <span className="ml-auto text-[9px] text-muted-foreground">{f.trigger}</span>
                </button>
              ))}
            </>
          )}
          {otherFlows.length > 0 && (
            <>
              <div className="px-3 py-1.5 text-[10px] font-semibold text-muted-foreground uppercase tracking-wider bg-muted/50">
                Other flows
              </div>
              {otherFlows.map((f) => (
                <button
                  key={f.id}
                  className="w-full px-3 py-1.5 text-xs text-left hover:bg-accent flex items-center gap-2"
                  onClick={() => {
                    onSelect(f.id)
                    setOpen(false)
                  }}
                >
                  <Zap className="h-3 w-3 text-muted-foreground" />
                  <span className="truncate font-mono">{f.name}</span>
                  <span className="ml-auto text-[9px] text-muted-foreground">{f.trigger}</span>
                </button>
              ))}
            </>
          )}
          {flows.length === 0 && (
            <div className="px-3 py-4 text-xs text-center text-muted-foreground">
              No flows available
            </div>
          )}
        </div>
      </PopoverContent>
    </Popover>
  )
}
